import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MainSidebar from "@/shared/MainSidebar";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../@/components/ui/dropdown-menu";
import { Button } from "../../@/components/ui/button";
import { ChevronDown, MoreVertical } from "lucide-react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import AdminDataTable from "../components/adminDataTable";
import AddPathwayDialog from "@/forms/addPathway";
import { Badge } from "../../@/components/ui/badge";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { toast } from "sonner";
import { useAuth } from "@clerk/clerk-react";
import EditItemDialog from "@/forms/editAdminTable";
import EditPathwayDialog from "@/forms/editPathway";
import DeleteItemDialog from "@/forms/deleteItem";
import EnrollStudentsToPathway from "../forms/studentPathwayEnroll";
import { Spinner } from "../components/spinner";
import TableToolbar from "../components/tableToolbar";
import DeleteStudentPathwayEnrollment from "../forms/deleteStudentPathway";
import DefaultTable from "../components/defaultTable";
import Pagination from "../components/pagination";
import AddPathwayTypeDialog from "../forms/addPathwayType";
import EditPathwayTypeDialog from "../forms/editPathwayType";
import DeletePathwayTypeDialog from "../forms/deletePathwayType";
import PathwayBulkEnrollDialog from "@/forms/pathwayBulkEnrollDialog";
import EditPathwayEnrollmentDialog from "@/forms/editPathwayEnrollment";
import BatchEditPathwayEnrollmentDialog from "@/forms/batchEditPathwayEnrollment";
import BatchDeleteStudentPathwayEnrollmentDialog from "@/forms/batchDeleteStudentPathwayEnrollment";
import BatchDeletePathwaysDialog from "@/forms/batchDeletePathways";
import BatchDeletePathwayTypesDialog from "@/forms/batchDeletePathwayTypes";

function EnrolmentsSkeleton({ rows = 6 }) {
  return (
    <div className="w-full rounded-md border border-(--neutral-300) bg-(--surface-panel) overflow-hidden">
      {/* Table header */}
      <div className="px-4 py-3 border-b border-(--neutral-300) bg-(--neutral-50)">
        <div className="flex items-center gap-4">
          <div className="h-3 w-32 rounded bg-(--neutral-100) animate-pulse" />
          <div
            className="h-3 w-24 rounded bg-(--neutral-100) animate-pulse"
            style={{ animationDelay: "0.1s" }}
          />
          <div
            className="h-3 w-36 rounded bg-(--neutral-100) animate-pulse"
            style={{ animationDelay: "0.2s" }}
          />
          <div
            className="h-3 w-20 rounded bg-(--neutral-100) animate-pulse ml-auto"
            style={{ animationDelay: "0.3s" }}
          />
        </div>
      </div>

      {/* Table rows */}
      <div className="divide-y divide-(--neutral-100)">
        {Array.from({ length: rows }).map((_, idx) => (
          <div key={idx} className="px-4 py-3.5 flex items-center gap-4">
            <div
              className="h-4 rounded bg-(--neutral-100) animate-pulse"
              style={{
                width: `${80 + Math.random() * 40}px`,
                animationDelay: `${idx * 0.05}s`,
              }}
            />
            <div
              className="h-4 rounded bg-(--neutral-100) animate-pulse"
              style={{
                width: `${100 + Math.random() * 60}px`,
                animationDelay: `${idx * 0.05 + 0.1}s`,
              }}
            />
            <div
              className="h-4 flex-1 max-w-md rounded bg-(--neutral-100) animate-pulse"
              style={{ animationDelay: `${idx * 0.05 + 0.2}s` }}
            />
            <div
              className="h-6 w-20 rounded-full bg-(--neutral-100) animate-pulse"
              style={{ animationDelay: `${idx * 0.05 + 0.3}s` }}
            />
          </div>
        ))}
      </div>

      {/* Footer pagination hint */}
      <div className="px-4 py-3 border-t border-(--neutral-300) bg-(--neutral-50)">
        <div className="flex items-center justify-between">
          <div className="h-3 w-40 rounded bg-(--neutral-100) animate-pulse" />
          <div className="flex gap-2">
            <div className="h-8 w-8 rounded bg-(--neutral-100) animate-pulse" />
            <div
              className="h-8 w-8 rounded bg-(--neutral-100) animate-pulse"
              style={{ animationDelay: "0.1s" }}
            />
            <div
              className="h-8 w-8 rounded bg-(--neutral-100) animate-pulse"
              style={{ animationDelay: "0.2s" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ManagePathways() {
  const location = useLocation();
  const navigate = useNavigate();
  const allowedTabs = useMemo(
    () => new Set(["enrolment", "pathways", "pathway-types"]),
    []
  );
  const urlTab =
    typeof window !== "undefined"
      ? new URLSearchParams(location.search).get("tab")
      : null;
  const initialTab =
    (location.state && location.state.tab) || urlTab || "enrolment";
  const [activeTab, setActiveTab] = useState(initialTab);

  const [shouldRenderEnrolmentTab, setShouldRenderEnrolmentTab] = useState(
    initialTab === "enrolment"
  );
  const [shouldRenderPathwaysTab, setShouldRenderPathwaysTab] = useState(
    initialTab === "pathways"
  );
  const [shouldRenderPathwayTypesTab, setShouldRenderPathwayTypesTab] = useState(
    initialTab === "pathway-types"
  );

  useEffect(() => {
    if (activeTab === "enrolment") setShouldRenderEnrolmentTab(true);
    if (activeTab === "pathways") setShouldRenderPathwaysTab(true);
    if (activeTab === "pathway-types") setShouldRenderPathwayTypesTab(true);
  }, [activeTab]);

  useEffect(() => {
    if (!urlTab) return;
    if (!allowedTabs.has(urlTab)) return;
    if (urlTab === activeTab) return;
    setActiveTab(urlTab);
  }, [urlTab, allowedTabs, activeTab]);
  const [pathways, setPathways] = useState([]);
  const [pathwayList, setPathwayList] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enrolmentsLoading, setEnrolmentsLoading] = useState(false);
  const [pathwayListLoading, setPathwayListLoading] = useState(false);
  const [enrolmentCountsLoading, setEnrolmentCountsLoading] = useState(false);
  const [enrolmentCountsByPathwayId, setEnrolmentCountsByPathwayId] = useState({});
  const [enrolmentsSearchResults, setEnrolmentsSearchResults] = useState([]);
  const [enrolmentsSearchResultsKey, setEnrolmentsSearchResultsKey] = useState(null);
  const [enrolmentsSearchLoading, setEnrolmentsSearchLoading] = useState(false);
  const enrolmentsSearchFetchRef = useRef({ key: null, promise: null, runId: 0 });
  const pagePaginationStorageKey = "pathway_enrolments_page_pagination_supported";
  const [pagePaginationSupported, setPagePaginationSupported] = useState(() => {
    try {
      if (typeof window === "undefined") return true;
      const raw = window.localStorage.getItem(pagePaginationStorageKey);
      if (raw == null) return true;
      return raw === "true";
    } catch {
      return true;
    }
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editType, setEditType] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [enrolmentPage, setEnrolmentPage] = useState(1);
  const [enrolmentRowsPerPage, setEnrolmentRowsPerPage] = useState(10);
  const [enrolmentsPagination, setEnrolmentsPagination] = useState({
    total_count: 0,
    total_pages: 1,
    current_page: 1,
    page_size: 10,
    has_next: false,
    has_previous: false,
  });
  const [pathwaySearch, setPathwaySearch] = useState("");
  const [pathwayFilter, setPathwayFilter] = useState("all");
  const [pathwaySort, setPathwaySort] = useState("all");
  const [enrolmentSearch, setEnrolmentSearch] = useState("");
  const [enrolmentCategoryFilter, setEnrolmentCategoryFilter] = useState("all");
  const [pendingEnrolmentCategoryFilter, setPendingEnrolmentCategoryFilter] =
    useState("all");
  const [enrolmentSort, setEnrolmentSort] = useState("all");
  const [deletePathwayDialogOpen, setDeletePathwayDialogOpen] = useState(false);
  const [selectedPathwayEnrollment, setSelectedPathwayEnrollment] =
    useState(null);
  const [editPathwayEnrollmentDialogOpen, setEditPathwayEnrollmentDialogOpen] =
    useState(false);
  const [selectedPathwayEnrollmentToEdit, setSelectedPathwayEnrollmentToEdit] =
    useState(null);

  const [batchEditPathwayEnrolmentsOpen, setBatchEditPathwayEnrolmentsOpen] =
    useState(false);
  const [selectedPathwayEnrolmentsForBatchEdit, setSelectedPathwayEnrolmentsForBatchEdit] =
    useState([]);
  const [batchDeletePathwayEnrolmentsOpen, setBatchDeletePathwayEnrolmentsOpen] =
    useState(false);
  const [selectedPathwayEnrolmentsForBatchDelete, setSelectedPathwayEnrolmentsForBatchDelete] =
    useState([]);
  const [enrolmentsSelectionResetKey, setEnrolmentsSelectionResetKey] =
    useState(0);

  const [batchDeletePathwaysOpen, setBatchDeletePathwaysOpen] = useState(false);
  const [selectedPathwaysForBatchDelete, setSelectedPathwaysForBatchDelete] =
    useState([]);
  const [pathwaysSelectionResetKey, setPathwaysSelectionResetKey] = useState(0);
  const [pathwayTypes, setPathwayTypes] = useState([]);
  const [pathwayTypesLoading, setPathwayTypesLoading] = useState(false);
  const [pathwayTypesSearch, setPathwayTypesSearch] = useState("");
  const [pathwayTypesPage, setPathwayTypesPage] = useState(1);
  const [pathwayTypesRowsPerPage, setPathwayTypesRowsPerPage] = useState(10);
  const [selectedPathwayTypeIds, setSelectedPathwayTypeIds] = useState(() => new Set());
  const [editPathwayTypeDialogOpen, setEditPathwayTypeDialogOpen] =
    useState(false);
  const [selectedPathwayType, setSelectedPathwayType] = useState(null);
  const [deletePathwayTypeDialogOpen, setDeletePathwayTypeDialogOpen] =
    useState(false);
  const [selectedPathwayTypeToDelete, setSelectedPathwayTypeToDelete] =
    useState(null);
  const [batchDeletePathwayTypesOpen, setBatchDeletePathwayTypesOpen] =
    useState(false);
  const [selectedPathwayTypesForBatchDelete, setSelectedPathwayTypesForBatchDelete] =
    useState([]);
  const [pathwayBulkDialogOpen, setPathwayBulkDialogOpen] = useState(false);
  const { getToken } = useAuth();

  const pathwayListFetchRef = useRef({ key: null, promise: null, runId: 0, lastSuccessKey: null });
  const pathwaysFetchRef = useRef({ key: null, promise: null, runId: 0, lastSuccessKey: null });
  const pathwayTypesFetchRef = useRef({ key: null, promise: null, runId: 0, lastSuccessKey: null });
  const enrolmentsFetchRef = useRef({ key: null, promise: null, runId: 0, lastSuccessKey: null });
  const enrolmentCountsFetchRef = useRef({ key: null, promise: null, runId: 0, lastSuccessKey: null });

  const formatLabel = (value, fallback = "Unknown") => {
    if (value == null) return fallback;
    const upper = String(value).trim().toUpperCase();
    if (!upper) return fallback;
    return upper
      .replace(/[_-]+/g, " ")
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase());
  };

  const logAxiosError = (context, error) => {
    const status = error?.response?.status;
    const statusText = error?.response?.statusText;
    const method = error?.config?.method;
    const url = error?.config?.baseURL
      ? `${error.config.baseURL}${error?.config?.url || ""}`
      : error?.config?.url;
    const params = error?.config?.params;
    const responseData = error?.response?.data;

    console.groupCollapsed(`❌ ${context} (AxiosError)`);
    console.log({
      message: error?.message,
      code: error?.code,
      method,
      url,
      params,
      status,
      statusText,
      responseData,
    });
    if (error?.stack) console.log("stack", error.stack);
    console.groupEnd();
  };

  const maybeDisablePagePagination = (context, error) => {
    const status = error?.response?.status;
    const params = error?.config?.params;
    const attemptedPageParams = params && ("page" in params || "page_size" in params);

    if ((status === 500 || status === 400) && attemptedPageParams && pagePaginationSupported) {
      setPagePaginationSupported(false);
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(pagePaginationStorageKey, "false");
        }
      } catch {
        // ignore storage failures
      }
      console.warn(
        `Enrolments: disabling server page/page_size pagination due to API error (${context}). Using fallback strategy.`
      );
      return true;
    }

    return false;
  };

  const parseEnrollmentsResponse = (response) => {
    const payload = response?.data?.enrollments ?? response?.data;
    const rowsRaw = Array.isArray(payload)
      ? payload
      : (payload?.data ?? payload?.enrollments ?? []);

    const paginationRaw =
      (!Array.isArray(payload) ? payload?.pagination : null) ??
      response?.data?.pagination ??
      {};

    return {
      rowsRaw: Array.isArray(rowsRaw) ? rowsRaw : [],
      paginationRaw: paginationRaw && typeof paginationRaw === "object" ? paginationRaw : {},
    };
  };

  const safeGetPathwayEnrollmentsPage = async ({
    pathwayId,
    page,
    pageSize,
    reqConfig,
    contextLabel,
  }) => {
    const baseUrl = api_endpoint + `/pathway/enrollments/${pathwayId}`;

    const safePage = Math.max(1, Number(page) || 1);
    const safePageSize = Math.max(1, Number(pageSize) || 10);

    // 1) Prefer server pagination using page/page_size.
    if (pagePaginationSupported) {
      try {
        const res = await axios.get(baseUrl, {
          ...reqConfig,
          params: { page: safePage, page_size: safePageSize },
        });
        return parseEnrollmentsResponse(res);
      } catch (err) {
        const disabled = maybeDisablePagePagination(contextLabel, err);
        if (!disabled) {
          logAxiosError(`${contextLabel} (server page/page_size failed)`, err);
        }
      }
    }

    // 2) Fallback: fetch without params.
    // NOTE: If the backend always returns only page 1 by default, true paging requires server params.
    const resNoParams = await axios.get(baseUrl, reqConfig);
    const parsed = parseEnrollmentsResponse(resNoParams);

    const totalCount =
      parsed.paginationRaw?.total_count != null
        ? Number(parsed.paginationRaw.total_count)
        : parsed.rowsRaw.length;

    const start = (safePage - 1) * safePageSize;
    const end = start + safePageSize;

    return {
      rowsRaw: parsed.rowsRaw.slice(start, end),
      paginationRaw: {
        total_count: Number.isFinite(totalCount) ? totalCount : parsed.rowsRaw.length,
        total_pages: Math.max(1, Math.ceil((Number.isFinite(totalCount) ? totalCount : parsed.rowsRaw.length) / safePageSize)),
        current_page: safePage,
        page_size: safePageSize,
        has_next: end < (Number.isFinite(totalCount) ? totalCount : parsed.rowsRaw.length),
        has_previous: start > 0,
      },
    };
  };

  const normalizeSearchTerms = (raw) => {
    const s = String(raw || "").toLowerCase().trim();
    if (!s) return [];
    return s.split(/\s+/g).map((t) => t.trim()).filter(Boolean);
  };

  const matchesSearchTerms = (terms, row) => {
    if (!Array.isArray(terms) || terms.length === 0) return true;

    const name = String(row?.name || "").toLowerCase();
    const email = String(row?.email || "").toLowerCase();
    const pathway = String(row?.pathway || "").toLowerCase();
    const haystack = `${name} ${email} ${pathway}`;

    // Require every term to match somewhere in name/email/pathway.
    return terms.every((t) => haystack.includes(t));
  };

  const formatEnrolmentRow = (item) => ({
    id: `${item.user_id}-${item.pathway_id}`,
    user_id: item.user_id,
    name:
      `${item.first_name || ""} ${item.last_name || ""}`.trim() ||
      "Not available",
    email: item.email || "Not available",
    status: item.status || "unknown",
    pathway: item.pathway_title || "Not available",
    pathway_id: Number(item.pathway_id),
  });

  // NOTE: Must be declared before it is referenced by other hooks' dependency arrays.
  const fetchPathwayList = useCallback(async ({ force = true } = {}) => {
    const key = "pathwayList";
    const ref = pathwayListFetchRef.current;

    if (!force && pathwayList.length > 0 && ref.lastSuccessKey === key) return pathwayList;
    if (ref.key === key && ref.promise) return ref.promise;

    const runId = ++ref.runId;
    setPathwayListLoading(true);

    const promise = (async () => {
      const response = await axios.get(api_endpoint + "/pathway/");
      const fetched = response.data?.pathways || [];
      const list = (Array.isArray(fetched) ? fetched : [])
        .map((p) => ({
          id: p?.id,
          title: p?.title,
        }))
        .filter((p) => p.id != null && String(p.title || "").trim().length);

      if (runId !== pathwayListFetchRef.current.runId) return list;
      setPathwayList(list);
      return list;
    })();

    ref.key = key;
    ref.promise = promise;

    try {
      const out = await promise;
      if (runId === pathwayListFetchRef.current.runId) pathwayListFetchRef.current.lastSuccessKey = key;
      return out;
    } catch (error) {
      console.error("Failed to fetch pathway list", error);
      if (runId === pathwayListFetchRef.current.runId && pathwayList.length === 0) setPathwayList([]);
      return [];
    } finally {
      if (runId === pathwayListFetchRef.current.runId) setPathwayListLoading(false);
      if (pathwayListFetchRef.current.promise === promise) pathwayListFetchRef.current.promise = null;
    }
  }, [pathwayList.length, pathwayListFetchRef]);

  const ensurePathwayList = useCallback(async () => {
    if (Array.isArray(pathwayList) && pathwayList.length > 0) return pathwayList;
    return await fetchPathwayList();
  }, [fetchPathwayList, pathwayList]);

  const fetchGlobalEnrolmentsSearch = useCallback(async ({ query, pathwayFilterValue } = {}) => {
    const trimmed = String(query || "").trim();
    const terms = normalizeSearchTerms(trimmed);
    if (terms.length === 0) return [];

    const token = await getToken();
    if (!token) return [];

    const resolvedPathwayFilter = String(pathwayFilterValue || enrolmentCategoryFilter || "all");
    const resolvedPathwayId = resolvedPathwayFilter !== "all" ? Number(resolvedPathwayFilter) : null;

    const reqConfig = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    const resultByKey = new Map();

    const addMatches = (items) => {
      const rows = Array.isArray(items) ? items : [];
      for (const item of rows) {
        const formatted = formatEnrolmentRow(item);
        if (!matchesSearchTerms(terms, formatted)) continue;
        const key = `${formatted.user_id}-${formatted.pathway_id}`;
        if (!resultByKey.has(key)) resultByKey.set(key, formatted);
      }
    };

    // Fetch all pages for a pathway using the same paging strategy as the table.
    // This avoids global-search returning empty when the backend rejects/ignores page params.
    const scanPathwayEnrolments = async ({ pathwayId, contextLabel }) => {
      const pageSize = 200;
      const maxPages = 200;
      const seen = new Set();

      for (let page = 1; page <= maxPages; page += 1) {
        const parsed = await safeGetPathwayEnrollmentsPage({
          pathwayId,
          page,
          pageSize,
          reqConfig,
          contextLabel: `${contextLabel} (page ${page})`,
        });

        const items = Array.isArray(parsed?.rowsRaw) ? parsed.rowsRaw : [];
        if (items.length === 0) break;

        // Safety: if the backend ignores pagination and keeps returning the same page, stop.
        let newCount = 0;
        for (const item of items) {
          const k = `${item?.user_id ?? ""}-${item?.pathway_id ?? pathwayId}`;
          if (!seen.has(k)) {
            seen.add(k);
            newCount += 1;
          }
        }
        if (newCount === 0) break;

        addMatches(items);

        if (items.length < pageSize) break;
      }
    };

    if (resolvedPathwayId != null && Number.isFinite(resolvedPathwayId)) {
      await scanPathwayEnrolments({
        pathwayId: resolvedPathwayId,
        contextLabel: `Global search enrolments fetch for pathway ${resolvedPathwayId}`,
      });
      return Array.from(resultByKey.values());
    }

    // All pathways: search across every pathway's enrolments.
    const resolvedPathwayList = await ensurePathwayList();
    const ids = (Array.isArray(resolvedPathwayList) ? resolvedPathwayList : [])
      .map((p) => Number(p?.id))
      .filter((id) => Number.isFinite(id));

    if (ids.length === 0) return [];

    const concurrency = 4;
    let cursor = 0;

    const worker = async () => {
      while (cursor < ids.length) {
        const idx = cursor;
        cursor += 1;
        const pathwayId = ids[idx];
        try {
          await scanPathwayEnrolments({
            pathwayId,
            contextLabel: `Global search enrolments fetch for pathway ${pathwayId}`,
          });
        } catch (err) {
          logAxiosError(`Global search enrolments fetch failed for pathway ${pathwayId}`, err);
        }
      }
    };

    const workers = Array.from({ length: Math.min(concurrency, ids.length) }, () => worker());
    await Promise.all(workers);

    return Array.from(resultByKey.values());
  }, [ensurePathwayList, enrolmentCategoryFilter, getToken]);

  const safeGetPathwayEnrollmentsOffsetSlice = async ({
    pathwayId,
    offset,
    take,
    pageSize,
    reqConfig,
    contextLabel,
  }) => {
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeTake = Math.max(0, Number(take) || 0);
    const safePageSize = Math.max(1, Number(pageSize) || 10);
    if (safeTake === 0) return [];

    const firstPage = Math.floor(safeOffset / safePageSize) + 1;
    const within = safeOffset % safePageSize;

    const first = await safeGetPathwayEnrollmentsPage({
      pathwayId,
      page: firstPage,
      pageSize: safePageSize,
      reqConfig,
      contextLabel: `${contextLabel} (page ${firstPage})`,
    });

    let rows = first.rowsRaw.slice(within, within + safeTake);
    const remaining = safeTake - rows.length;
    if (remaining <= 0) return rows;

    const second = await safeGetPathwayEnrollmentsPage({
      pathwayId,
      page: firstPage + 1,
      pageSize: safePageSize,
      reqConfig,
      contextLabel: `${contextLabel} (page ${firstPage + 1})`,
    });

    rows = rows.concat(second.rowsRaw.slice(0, remaining));
    return rows;
  };

  const fetchEnrolmentCounts = useCallback(async ({ token, force = true, pathwayIds } = {}) => {
    const ids = Array.isArray(pathwayIds)
      ? pathwayIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id))
      : (Array.isArray(pathwayList) ? pathwayList : [])
          .map((p) => Number(p?.id))
          .filter((id) => Number.isFinite(id));

    if (ids.length === 0) return {};

    const key = `counts:${ids.join(",")}`;
    const ref = enrolmentCountsFetchRef.current;
    if (!force && ref.lastSuccessKey === key) return enrolmentCountsByPathwayId || {};
    if (ref.key === key && ref.promise) return ref.promise;

    const runId = ++ref.runId;

    setEnrolmentCountsLoading(true);
    const promise = (async () => {
      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const items = ids;

      // Simple concurrency pool to avoid spiking requests.
      const concurrency = 6;
      const result = {};
      let cursor = 0;

      const worker = async () => {
        while (cursor < items.length) {
          const idx = cursor;
          cursor += 1;
          const pathwayId = items[idx];
          try {
            const parsed = await safeGetPathwayEnrollmentsPage({
              pathwayId,
              page: 1,
              pageSize: 10,
              reqConfig,
              contextLabel: `Count fetch for pathway ${pathwayId}`,
            });

            const countRaw =
              parsed.paginationRaw?.total_count != null
                ? Number(parsed.paginationRaw.total_count)
                : parsed.rowsRaw.length;

            result[pathwayId] = Number.isFinite(countRaw) ? countRaw : 0;
          } catch (err) {
            // If a single pathway fails, treat its count as 0 to keep pagination usable.
            logAxiosError(`Failed to fetch enrolment count for pathway ${pathwayId}`, err);
            result[pathwayId] = 0;
          }
        }
      };

      const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => worker());
      await Promise.all(workers);

      if (runId !== enrolmentCountsFetchRef.current.runId) return result;
      setEnrolmentCountsByPathwayId(result);
      return result;
    })();

    ref.key = key;
    ref.promise = promise;
    try {
      const out = await promise;
      if (runId === enrolmentCountsFetchRef.current.runId) {
        enrolmentCountsFetchRef.current.lastSuccessKey = key;
      }
      return out;
    } finally {
      if (runId === enrolmentCountsFetchRef.current.runId) setEnrolmentCountsLoading(false);
      if (enrolmentCountsFetchRef.current.promise === promise) enrolmentCountsFetchRef.current.promise = null;
    }
  }, [enrolmentCountsByPathwayId, pathwayList]);

  // For Pathways tab

  const [pendingPathwayFilter, setPendingPathwayFilter] =
    useState(pathwayFilter);
  const [pendingPathwaySort, setPendingPathwaySort] = useState(pathwaySort);

  // For Enrolments tab

  const [pendingEnrolmentSort, setPendingEnrolmentSort] =
    useState(enrolmentSort);

  // For Pathway Types tab

  const [pathwayTypesFilter, setPathwayTypesFilter] = useState("all");
  const [pendingPathwayTypesFilter, setPendingPathwayTypesFilter] =
    useState("all");
  const [pathwayTypesSort, setPathwayTypesSort] = useState("type");
  const [pendingPathwayTypesSort, setPendingPathwayTypesSort] =
    useState("type");

  useEffect(() => {
    setPendingPathwayTypesFilter(pathwayTypesFilter);
  }, [pathwayTypesFilter]);
  useEffect(() => {
    setPendingPathwayTypesSort(pathwayTypesSort);
  }, [pathwayTypesSort]);

  useEffect(() => {
    setPendingPathwayFilter(pathwayFilter);
  }, [pathwayFilter]);
  useEffect(() => {
    setPendingPathwaySort(pathwaySort);
  }, [pathwaySort]);

  useEffect(() => {
    setPendingEnrolmentSort(enrolmentSort);
  }, [enrolmentSort]);
  useEffect(() => {
    setPendingEnrolmentCategoryFilter(enrolmentCategoryFilter);
  }, [enrolmentCategoryFilter]);

  const fetchPathways = useCallback(async ({ force = true } = {}) => {
    const key = "pathways";
    const ref = pathwaysFetchRef.current;

    if (!force && pathways.length > 0 && ref.lastSuccessKey === key) return;
    if (ref.key === key && ref.promise) return ref.promise;

    const runId = ++ref.runId;
    setLoading(true);

    const promise = (async () => {
      const response = await axios.get(api_endpoint + "/pathway/");
      const fetched = response.data?.pathways || [];

      // First paint fast: show pathways immediately without N+1 enrolment-count lookups.
      const base = (Array.isArray(fetched) ? fetched : []).map((item) => ({
        id: item.id,
        name: item.title || "Not available",
        description: item.description || "Not available",
        type: item.level || "Not available",
        category: item.level || "Not available",
        associatedCourses: "",
        courses: [],
        courseCount: undefined,
      }));

      if (runId !== pathwaysFetchRef.current.runId) return;
      setPathways(base);

      // Hydrate course counts in the background in small batches.
      const concurrency = 6;
      let cursor = 0;

      const hydrateWorker = async () => {
        while (cursor < base.length) {
          const idx = cursor;
          cursor += 1;
          const pathway = base[idx];
          const id = pathway?.id;
          if (id == null) continue;
          try {
            const res = await axios.get(api_endpoint + `/pathway/${id}/courses`);
            const associatedCoursesArr = res.data?.courses || [];
            const associatedTitles = associatedCoursesArr
              .map((c) => c.title)
              .filter(Boolean)
              .join(", ")
              .toUpperCase();
            const associatedCourseIds = associatedCoursesArr.map((c) => c.id);
            const courseCount = associatedCoursesArr.length;

            if (runId !== pathwaysFetchRef.current.runId) return;
            setPathways((prev) =>
              (Array.isArray(prev) ? prev : []).map((p) =>
                p?.id === id
                  ? {
                      ...p,
                      associatedCourses: associatedTitles,
                      courses: associatedCourseIds,
                      courseCount,
                    }
                  : p
              )
            );
          } catch (err) {
            // If a pathway's course fetch fails, keep it blank rather than failing the whole page.
            console.warn(`Failed to fetch courses for pathway ${id}`);
          }
        }
      };

      const workers = Array.from(
        { length: Math.min(concurrency, base.length) },
        () => hydrateWorker()
      );
      await Promise.all(workers);
    })();

    ref.key = key;
    ref.promise = promise;
    try {
      await promise;
      if (runId === pathwaysFetchRef.current.runId) pathwaysFetchRef.current.lastSuccessKey = key;
    } catch (error) {
      console.error("Failed to fetch pathways", error);
      if (runId === pathwaysFetchRef.current.runId && pathways.length === 0) setPathways([]);
    } finally {
      if (runId === pathwaysFetchRef.current.runId) setLoading(false);
      if (pathwaysFetchRef.current.promise === promise) pathwaysFetchRef.current.promise = null;
    }
  }, [pathways.length, pathwaysFetchRef]);

  useEffect(() => {
    if (!batchEditPathwayEnrolmentsOpen) return;
    if (Array.isArray(pathwayList) && pathwayList.length > 0) return;
    ensurePathwayList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchEditPathwayEnrolmentsOpen]);

  const fetchEnrolments = useCallback(async ({ force = true } = {}) => {
    const page = Math.max(1, Number(enrolmentPage) || 1);
    const pageSize = Math.max(1, Number(enrolmentRowsPerPage) || 10);
    const pathwayFilterValue = String(enrolmentCategoryFilter || "all");
    const key = JSON.stringify({
      page,
      pageSize,
      pathwayFilterValue,
      pagePaginationSupported,
    });

    const ref = enrolmentsFetchRef.current;
    if (!force && ref.lastSuccessKey === key) return;
    if (ref.key === key && ref.promise) return ref.promise;

    const runId = ++ref.runId;
    setEnrolmentsLoading(true);

    const promise = (async () => {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const pathwayId = enrolmentCategoryFilter !== "all" ? Number(enrolmentCategoryFilter) : null;

      let rowsRaw = [];
      let paginationRaw = {};

      if (pathwayId != null && Number.isFinite(pathwayId)) {
        ({ rowsRaw, paginationRaw } = await safeGetPathwayEnrollmentsPage({
          pathwayId,
          page,
          pageSize,
          reqConfig,
          contextLabel: `Enrolments fetch for pathway ${pathwayId}`,
        }));
      } else {
        // Backend doesn't allow GET /pathway/enrollments (405), so we aggregate pages across pathways.
        // Fast path for page 1: fill the current page without blocking on count prefetch.
        const resolvedPathwayList = await ensurePathwayList();
        if (!Array.isArray(resolvedPathwayList) || resolvedPathwayList.length === 0) {
          console.warn("Enrolments: pathway list is empty; cannot aggregate all-pathways enrolments yet.");
          rowsRaw = [];
          paginationRaw = {
            total_count: 0,
            total_pages: 1,
            current_page: page,
            page_size: pageSize,
            has_next: false,
            has_previous: page > 1,
          };
          console.log("Fetched pathway enrolments:", { rows: rowsRaw, pagination: paginationRaw, pathwayId });
          setEnrolments([]);
          setEnrolmentsPagination({
            total_count: 0,
            total_pages: 1,
            current_page: page,
            page_size: pageSize,
            has_next: false,
            has_previous: page > 1,
          });
          return;
        }

        const orderedPathwayIds = (Array.isArray(resolvedPathwayList) ? resolvedPathwayList : [])
          .map((p) => Number(p?.id))
          .filter((id) => Number.isFinite(id))
          .sort((a, b) => a - b);

        if (page === 1 && Object.keys(enrolmentCountsByPathwayId || {}).length === 0) {
          const pageRows = [];
          for (const id of orderedPathwayIds) {
            if (pageRows.length >= pageSize) break;
            const parsed = await safeGetPathwayEnrollmentsPage({
              pathwayId: id,
              page: 1,
              pageSize,
              reqConfig,
              contextLabel: `Aggregate enrolments fast-fill for pathway ${id}`,
            });
            pageRows.push(...parsed.rowsRaw);
          }

          rowsRaw = pageRows.slice(0, pageSize);
          paginationRaw = {
            // Unknown until counts backfill finishes; leave nullish so we can fall back to a non-zero lower bound.
            total_count: null,
            total_pages: null,
            current_page: 1,
            page_size: pageSize,
            has_next: false,
            has_previous: false,
          };

          // Backfill counts in the background and update pagination once known.
          fetchEnrolmentCounts({ token, pathwayIds: orderedPathwayIds, force: true }).catch(() => {});
        } else {
          const counts =
            Object.keys(enrolmentCountsByPathwayId || {}).length > 0
              ? enrolmentCountsByPathwayId
              : await fetchEnrolmentCounts({ token, pathwayIds: orderedPathwayIds, force: true });

          const totalCount = orderedPathwayIds.reduce(
            (sum, id) => sum + Number(counts?.[id] ?? 0),
            0
          );
          const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

        const globalSkip = (page - 1) * pageSize;
        const globalTake = pageSize;

        let remainingSkip = globalSkip;
        let remainingTake = globalTake;
        const pageRows = [];

        for (const id of orderedPathwayIds) {
          const pathwayCount = Number(counts?.[id] ?? 0);
          if (pathwayCount <= 0) continue;

          if (remainingSkip >= pathwayCount) {
            remainingSkip -= pathwayCount;
            continue;
          }

          const slice = await safeGetPathwayEnrollmentsOffsetSlice({
            pathwayId: id,
            offset: remainingSkip,
            take: remainingTake,
            pageSize,
            reqConfig,
            contextLabel: `Aggregate enrolments fetch for pathway ${id}`,
          });

          pageRows.push(...slice);
          remainingTake = Math.max(0, remainingTake - slice.length);
          remainingSkip = 0;
          if (remainingTake === 0) break;
        }

          rowsRaw = pageRows;
          paginationRaw = {
            total_count: totalCount,
            total_pages: totalPages,
            current_page: page,
            page_size: pageSize,
            has_next: page < totalPages,
            has_previous: page > 1,
          };
        }
      }

      console.log("Fetched pathway enrolments:", { rows: rowsRaw, pagination: paginationRaw, pathwayId });

      const formatted = (Array.isArray(rowsRaw) ? rowsRaw : []).map(formatEnrolmentRow);
      if (runId !== enrolmentsFetchRef.current.runId) return;

      setEnrolments(formatted);

      // Some endpoints (or the all-pathways fast-fill) can report total_count as 0/undefined even when rows exist.
      // Use a non-zero lower bound until we can backfill a real total.
      const lowerBoundTotalCount = (page - 1) * pageSize + formatted.length;

      const rawTotalCount = Number(paginationRaw?.total_count);
      const total_count =
        Number.isFinite(rawTotalCount) && rawTotalCount > 0
          ? rawTotalCount
          : lowerBoundTotalCount;

      const rawTotalPages = Number(paginationRaw?.total_pages);
      const total_pages =
        Number.isFinite(rawTotalPages) && rawTotalPages > 0
          ? rawTotalPages
          : Math.max(1, Math.ceil(total_count / pageSize));

      const current_page = Math.max(1, Number(paginationRaw?.current_page ?? page ?? 1));
      const page_size = Math.max(1, Number(paginationRaw?.page_size ?? pageSize));

      const has_previous =
        typeof paginationRaw?.has_previous === "boolean"
          ? paginationRaw.has_previous
          : current_page > 1;

      const has_next =
        typeof paginationRaw?.has_next === "boolean"
          ? paginationRaw.has_next
          : current_page < total_pages;

      setEnrolmentsPagination({
        total_count,
        total_pages,
        current_page,
        page_size,
        has_next,
        has_previous,
      });
    })();

    ref.key = key;
    ref.promise = promise;
    try {
      await promise;
      if (runId === enrolmentsFetchRef.current.runId) enrolmentsFetchRef.current.lastSuccessKey = key;
    } catch (error) {
      logAxiosError("Failed to fetch pathway enrolments", error);
      if (runId === enrolmentsFetchRef.current.runId && enrolments.length === 0) {
        setEnrolments([]);
        setEnrolmentsPagination({
          total_count: 0,
          total_pages: 1,
          current_page: 1,
          page_size: enrolmentRowsPerPage,
          has_next: false,
          has_previous: false,
        });
      }
    } finally {
      if (runId === enrolmentsFetchRef.current.runId) setEnrolmentsLoading(false);
      if (enrolmentsFetchRef.current.promise === promise) enrolmentsFetchRef.current.promise = null;
    }
  }, [
    enrolmentCategoryFilter,
    enrolmentCountsByPathwayId,
    enrolmentPage,
    enrolmentRowsPerPage,
    enrolments.length,
    enrolmentsFetchRef,
    enrolmentRowsPerPage,
    ensurePathwayList,
    fetchEnrolmentCounts,
    getToken,
    pagePaginationSupported,
    pathwayListLoading,
  ]);

  const enrolmentsSearchActive = Boolean(String(enrolmentSearch || "").trim());
  const enrolmentsSearchKey = useMemo(
    () =>
      JSON.stringify({
        query: String(enrolmentSearch || "").trim().toLowerCase(),
        pathway: String(enrolmentCategoryFilter || "all"),
      }),
    [enrolmentSearch, enrolmentCategoryFilter]
  );

  // Global search (across all pages), debounced.
  useEffect(() => {
    if (activeTab !== "enrolment") return;

    const query = String(enrolmentSearch || "").trim();
    if (!query) {
      setEnrolmentsSearchResults([]);
      setEnrolmentsSearchResultsKey(null);
      setEnrolmentsSearchLoading(false);
      return;
    }

    // Always render instant local filtering (like Manage Cohorts).
    // Only run the global API search once the user pauses typing.
    const queryLower = query.toLowerCase();
    const pathwayFilterValue = String(enrolmentCategoryFilter || "all");
    const shouldRunGlobal =
      pathwayFilterValue !== "all" ? queryLower.length >= 2 : queryLower.length >= 3;

    // Clear old global results for this new query so we never show stale rows.
    setEnrolmentsSearchResults([]);
    setEnrolmentsSearchResultsKey(null);
    setEnrolmentsSearchLoading(false);

    if (!shouldRunGlobal) return;

    const key = JSON.stringify({ query: queryLower, pathway: pathwayFilterValue });
    const ref = enrolmentsSearchFetchRef.current;

    // Dedupe: if the same global search is already in-flight, don't start another.
    if (ref.key === key && ref.promise) {
      setEnrolmentsSearchLoading(true);
      return;
    }

    const runId = ++ref.runId;

    const handle = setTimeout(() => {
      if (runId !== enrolmentsSearchFetchRef.current.runId) return;
      setEnrolmentsSearchLoading(true);

      const promise = (async () => {
        try {
          const out = await fetchGlobalEnrolmentsSearch({
            query,
            pathwayFilterValue: enrolmentCategoryFilter,
          });
          if (runId !== enrolmentsSearchFetchRef.current.runId) return;
          setEnrolmentsSearchResults(Array.isArray(out) ? out : []);
          setEnrolmentsSearchResultsKey(key);
        } catch (err) {
          console.error("Global enrolments search failed", err);
          if (runId !== enrolmentsSearchFetchRef.current.runId) return;
          setEnrolmentsSearchResults([]);
          setEnrolmentsSearchResultsKey(key);
        } finally {
          if (runId === enrolmentsSearchFetchRef.current.runId) setEnrolmentsSearchLoading(false);
        }
      })();

      ref.key = key;
      ref.promise = promise;
    }, 600);

    return () => {
      clearTimeout(handle);
    };
  }, [activeTab, enrolmentSearch, enrolmentCategoryFilter, fetchGlobalEnrolmentsSearch]);

  const fetchPathwayTypes = useCallback(async ({ force = true } = {}) => {
    const key = "pathwayTypes";
    const ref = pathwayTypesFetchRef.current;
    if (!force && pathwayTypes.length > 0 && ref.lastSuccessKey === key) return;
    if (ref.key === key && ref.promise) return ref.promise;

    const runId = ++ref.runId;
    setPathwayTypesLoading(true);

    const promise = (async () => {
      const token = await getToken();
      if (!token) throw new Error("No authentication token found");

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.get(
        api_endpoint + "/pathway/levels",
        reqConfig
      );
      console.log("Fetched pathway types:", response.data);
      const types = response.data?.levels || [];
      if (runId !== pathwayTypesFetchRef.current.runId) return;
      setPathwayTypes(
        types.map((pt) => ({
          id: pt.id,
          type: pt.name,
          description: pt.description || "",
          pathways: pt.pathway_count || 0,
        }))
      );
    })();

    ref.key = key;
    ref.promise = promise;
    try {
      await promise;
      if (runId === pathwayTypesFetchRef.current.runId) pathwayTypesFetchRef.current.lastSuccessKey = key;
    } catch (error) {
      toast.error("Failed to fetch pathway types");
      if (runId === pathwayTypesFetchRef.current.runId && pathwayTypes.length === 0) setPathwayTypes([]);
    } finally {
      if (runId === pathwayTypesFetchRef.current.runId) setPathwayTypesLoading(false);
      if (pathwayTypesFetchRef.current.promise === promise) pathwayTypesFetchRef.current.promise = null;
    }
  }, [getToken, pathwayTypes.length, pathwayTypesFetchRef]);

  // Lazy load data per tab to avoid long timeouts
  useEffect(() => {
    if (activeTab === "pathways") {
      fetchPathways({ force: false });
      return;
    }
    if (activeTab === "pathway-types") {
      fetchPathwayTypes({ force: false });
      return;
    }
    if (activeTab === "enrolment") {
      fetchPathwayList({ force: false });
    }
  }, [activeTab, fetchPathwayList, fetchPathwayTypes, fetchPathways]);

  useEffect(() => {
    if (activeTab !== "enrolment") return;

    // While global search is active, we page locally over the search results.
    if (enrolmentsSearchActive) return;

    // Let the fetch function handle list readiness + dedupe, so tab switches don't cause reload loops.
    fetchEnrolments({ force: false });
  }, [activeTab, enrolmentPage, enrolmentRowsPerPage, enrolmentCategoryFilter, fetchEnrolments]);

  // When "All Pathways" is selected, we can render page 1 quickly and backfill pagination
  // once counts arrive (without re-fetching the table rows).
  useEffect(() => {
    if (activeTab !== "enrolment") return;
    if (String(enrolmentCategoryFilter || "all") !== "all") return;
    if (!Array.isArray(enrolments) || enrolments.length === 0) return;

    const counts = enrolmentCountsByPathwayId || {};
    if (Object.keys(counts).length === 0) return;

    const orderedPathwayIdsFromList = (Array.isArray(pathwayList) ? pathwayList : [])
      .map((p) => Number(p?.id))
      .filter((id) => Number.isFinite(id))
      .sort((a, b) => a - b);

    const orderedPathwayIds =
      orderedPathwayIdsFromList.length > 0
        ? orderedPathwayIdsFromList
        : Object.keys(counts)
            .map((k) => Number(k))
            .filter((id) => Number.isFinite(id))
            .sort((a, b) => a - b);

    if (orderedPathwayIds.length === 0) return;

    const pageSize = Math.max(1, Number(enrolmentRowsPerPage) || 10);
    const totalCount = orderedPathwayIds.reduce(
      (sum, id) => sum + Number(counts?.[id] ?? 0),
      0
    );
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

    setEnrolmentsPagination((prev) => {
      const next = {
        ...prev,
        total_count: totalCount,
        total_pages: totalPages,
        current_page: Math.max(1, Number(enrolmentPage) || 1),
        page_size: pageSize,
        has_next: (Number(enrolmentPage) || 1) < totalPages,
        has_previous: (Number(enrolmentPage) || 1) > 1,
      };
      // Avoid extra renders if nothing changes.
      const same =
        prev?.total_count === next.total_count &&
        prev?.total_pages === next.total_pages &&
        prev?.current_page === next.current_page &&
        prev?.page_size === next.page_size &&
        prev?.has_next === next.has_next &&
        prev?.has_previous === next.has_previous;
      return same ? prev : next;
    });
  }, [
    activeTab,
    enrolmentCategoryFilter,
    enrolmentCountsByPathwayId,
    enrolmentPage,
    enrolmentRowsPerPage,
    enrolments,
    pathwayList,
  ]);

  // Columns for pathways
  const pathwayColumns = [
    { key: "name", label: "Pathway" },
    {
      key: "description",
      label: "Description",
      render: (value) => (
        <span className="block max-w-100 truncate" title={value}>
          {value}
        </span>
      ),
    },
    {
      key: "type",
      label: "Type",
      render: (value) => (
        <Badge variant="" className="">
          {value ? formatLabel(value, "N/A") : "N/A"}
        </Badge>
      ),
    },
    { key: "courseCount", label: "Courses" },
  ];

  // Columns for pathway enrolment
  const enrolmentColumns = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    {
      key: "status",
      label: "Status",
      render: (value) => (
        <Badge className={String(value || "").toLowerCase() === "active" ? "" : ""}>
          {value ? formatLabel(value, "Unknown") : "Unknown"}
        </Badge>
      ),
    },
    { key: "pathway", label: "Pathway" },
  ];

  const enrolmentSortOptions = [
    { value: "all", label: "All" },
    { value: "name", label: "Name" },
    { value: "email", label: "Email" },
    { value: "status", label: "Status" },
    { value: "pathway", label: "Pathway" },
  ];

  const pathwayFields = [
    { key: "name", label: "Pathway Title", required: true },
    { key: "description", label: "Description", required: true },
    { key: "type", label: "Type", required: true },
    { key: "courses", label: "Associated Courses" },
  ];

  const pathwayFilterOptions = [
    { value: "all", label: "All Types" },
    ...Array.from(new Set(pathways.map((p) => p.type))).map((type) => ({
      value: type,
      label: type,
    })),
  ];

  const pathwaySortOptions = [
    { value: "all", label: "Sort by All" },
    { value: "name", label: "Pathway Name" },
    { value: "type", label: "Type" },
    { value: "courseCount", label: "Course Count" },
  ];

  const filteredSortedPathways = useMemo(() => {
    let result = pathways;
    // Filter
    if (pathwayFilter !== "all") {
      result = result.filter((p) => p.type === pathwayFilter);
    }
    // Search
    if (pathwaySearch.trim()) {
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(pathwaySearch.toLowerCase()) ||
          p.type.toLowerCase().includes(pathwaySearch.toLowerCase()) ||
          p.description.toLowerCase().includes(pathwaySearch.toLowerCase())
      );
    }

    // Sort
    if (pathwaySort !== "all") {
      result = [...result].sort((a, b) => {
        if (pathwaySort === "name") {
          return a.name.localeCompare(b.name, undefined, {
            sensitivity: "base",
          });
        }
        if (pathwaySort === "courseCount") {
          return b.courseCount - a.courseCount; // Sort course count descending
        }
        if (a[pathwaySort] < b[pathwaySort]) return -1;
        if (a[pathwaySort] > b[pathwaySort]) return 1;
        return 0;
      });
    }
    return result;
  }, [pathways, pathwayFilter, pathwaySearch, pathwaySort]);

  const filteredSortedEnrolments = useMemo(() => {
    let result = enrolments;

    if (enrolmentCategoryFilter !== "all") {
      result = result.filter((item) => Number(item.pathway_id) === Number(enrolmentCategoryFilter));
    }

    if (enrolmentSort !== "all") {
      result = [...result].sort((a, b) => {
        if (enrolmentSort === "name") return a.name.localeCompare(b.name);
        if (enrolmentSort === "status") return a.status.localeCompare(b.status);
        if (enrolmentSort === "pathway")
          return a.pathway.localeCompare(b.pathway);
        return 0;
      });
    }

    return result;
  }, [enrolments, enrolmentCategoryFilter, enrolmentSort]);

  const localSearchResults = useMemo(() => {
    if (!enrolmentsSearchActive) return [];
    const terms = normalizeSearchTerms(enrolmentSearch);
    const base = Array.isArray(enrolments) ? enrolments : [];
    return base.filter((row) => matchesSearchTerms(terms, row));
  }, [enrolmentsSearchActive, enrolmentSearch, enrolments]);

  const sortedLocalSearchResults = useMemo(() => {
    const base = Array.isArray(localSearchResults) ? localSearchResults : [];
    if (enrolmentSort === "all") return base;

    return [...base].sort((a, b) => {
      if (enrolmentSort === "name") return String(a.name || "").localeCompare(String(b.name || ""));
      if (enrolmentSort === "email") return String(a.email || "").localeCompare(String(b.email || ""));
      if (enrolmentSort === "status") return String(a.status || "").localeCompare(String(b.status || ""));
      if (enrolmentSort === "pathway") return String(a.pathway || "").localeCompare(String(b.pathway || ""));
      return 0;
    });
  }, [localSearchResults, enrolmentSort]);

  const sortedGlobalSearchResults = useMemo(() => {
    const isCurrent = enrolmentsSearchResultsKey === enrolmentsSearchKey;
    const base = isCurrent && Array.isArray(enrolmentsSearchResults) ? enrolmentsSearchResults : [];
    if (enrolmentSort === "all") return base;

    return [...base].sort((a, b) => {
      if (enrolmentSort === "name") return String(a.name || "").localeCompare(String(b.name || ""));
      if (enrolmentSort === "email") return String(a.email || "").localeCompare(String(b.email || ""));
      if (enrolmentSort === "status") return String(a.status || "").localeCompare(String(b.status || ""));
      if (enrolmentSort === "pathway") return String(a.pathway || "").localeCompare(String(b.pathway || ""));
      return 0;
    });
  }, [enrolmentsSearchKey, enrolmentsSearchResults, enrolmentsSearchResultsKey, enrolmentSort]);

  const sortedEffectiveSearchResults = useMemo(() => {
    if (!enrolmentsSearchActive) return [];
    return sortedGlobalSearchResults.length > 0
      ? sortedGlobalSearchResults
      : sortedLocalSearchResults;
  }, [enrolmentsSearchActive, sortedGlobalSearchResults, sortedLocalSearchResults]);

  const pagedEffectiveSearchResults = useMemo(() => {
    if (!enrolmentsSearchActive) return [];
    const page = Math.max(1, Number(enrolmentPage) || 1);
    const pageSize = Math.max(1, Number(enrolmentRowsPerPage) || 10);
    const start = (page - 1) * pageSize;
    return sortedEffectiveSearchResults.slice(start, start + pageSize);
  }, [enrolmentsSearchActive, enrolmentPage, enrolmentRowsPerPage, sortedEffectiveSearchResults]);

  const effectiveSearchTotalRows = sortedEffectiveSearchResults.length;
  const effectiveSearchTotalPages = Math.max(
    1,
    Math.ceil(effectiveSearchTotalRows / Math.max(1, Number(enrolmentRowsPerPage) || 10))
  );

  useEffect(() => {
    if (!enrolmentsSearchActive) return;
    if ((Number(enrolmentPage) || 1) <= effectiveSearchTotalPages) return;
    setEnrolmentPage(effectiveSearchTotalPages);
  }, [enrolmentsSearchActive, enrolmentPage, effectiveSearchTotalPages]);

  const enrolmentFilterOptions = [
    { value: "all", label: "All Pathways" },
    ...pathwayList.map((p) => ({
      value: String(p.id),
      label: p.title,
    })),
  ];

  const pathwayTypesColumns = [
    { key: "type", header: "Pathway Type" },
    { key: "description", header: "Description" },
    { key: "pathways", header: "Pathways" },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem
              onClick={() => {
                setSelectedPathwayType(row);
                setEditPathwayTypeDialogOpen(true);
              }}
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-(--color-primary)"
              onClick={() => {
                setSelectedPathwayTypeToDelete(row);
                setDeletePathwayTypeDialogOpen(true);
              }}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const filteredPathwayTypes = useMemo(() => {
    let result = pathwayTypes;
    // Filter
    if (pathwayTypesFilter !== "all") {
      result = result.filter((pt) => pt.type === pathwayTypesFilter);
    }
    // Search
    if (pathwayTypesSearch.trim()) {
      result = result.filter(
        (pt) =>
          pt.type.toLowerCase().includes(pathwayTypesSearch.toLowerCase()) ||
          pt.description
            .toLowerCase()
            .includes(pathwayTypesSearch.toLowerCase())
      );
    }
    // Sort
    if (pathwayTypesSort !== "all") {
      result = [...result].sort((a, b) => {
        if (pathwayTypesSort === "type" || pathwayTypesSort === "description") {
          return a[pathwayTypesSort].localeCompare(
            b[pathwayTypesSort],
            undefined,
            { sensitivity: "base" }
          );
        }
        if (pathwayTypesSort === "pathways") {
          return (b.pathways || 0) - (a.pathways || 0);
        }
        return 0;
      });
    }
    return result;
  }, [pathwayTypes, pathwayTypesFilter, pathwayTypesSearch, pathwayTypesSort]);

  const totalPathwayTypes = filteredPathwayTypes.length;
  const totalPathwayTypesPages = Math.max(
    1,
    Math.ceil(totalPathwayTypes / pathwayTypesRowsPerPage)
  );
  const paginatedPathwayTypes = filteredPathwayTypes.slice(
    (pathwayTypesPage - 1) * pathwayTypesRowsPerPage,
    pathwayTypesPage * pathwayTypesRowsPerPage
  );

  const paginatedPathwayTypesWithRowKeys = useMemo(
    () =>
      paginatedPathwayTypes.map((row) => ({
        ...row,
        rowKey: row?.id ?? row?._id ?? row?.type ?? JSON.stringify(row),
      })),
    [paginatedPathwayTypes]
  );

  const pagePathwayTypeKeys = useMemo(
    () => paginatedPathwayTypesWithRowKeys.map((row) => row.rowKey),
    [paginatedPathwayTypesWithRowKeys]
  );

  const toggleSelectPathwayType = (rowKey) =>
    setSelectedPathwayTypeIds((prev) => {
      const copy = new Set(prev);
      copy.has(rowKey) ? copy.delete(rowKey) : copy.add(rowKey);
      return copy;
    });

  const toggleSelectAllPathwayTypesOnPage = (checked) =>
    setSelectedPathwayTypeIds((prev) => {
      const copy = new Set(prev);
      if (checked) {
        pagePathwayTypeKeys.forEach((k) => copy.add(k));
        return copy;
      }
      pagePathwayTypeKeys.forEach((k) => copy.delete(k));
      return copy;
    });

  const handleEdit = (item, type) => {
    setEditItem(item);
    setEditType(type);
    setEditDialogOpen(true);
  };

  const handleDelete = (item, type = null) => {
    setDeleteItem(item);
    setDeleteType(type || activeTab); // track what kind of resource user is deleting
    setDeleteDialogOpen(true);
  };

  const handleSaveEdit = async (form) => {
    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      if (editType === "pathway") {
        const payload = {
          title: form.name,
          description: form.description || "Not Available",
          level: form.type,
          courses: Array.isArray(form.courses)
            ? form.courses.map((id) => Number(id))
            : [],
        };
        console.log("Update pathway payload:", payload);
        const response = await axios.put(
          api_endpoint + `/pathway/edit/${form.id}`,
          payload,
          reqConfig
        );
        console.log("Update pathway response:", response.data);
        toast.success("Pathway updated successfully");
        setEditDialogOpen(false);
        fetchPathways({ force: true });
      }
    } catch (error) {
      console.error("Failed to save edits:", error);
      toast.error("Failed to save changes. Please try again.");
    }
  };

  const handleConfirmDelete = async (item) => {
    setDeleteLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      let response;
      if (deleteType === "pathways") {
        response = await axios.delete(
          `${api_endpoint}/pathway/${item.id}`,
          reqConfig
        );
      }

      console.log("Delete response:", response.data);
      toast.success(`Item deleted successfully`);
      setDeleteDialogOpen(false);
      fetchPathways({ force: true }); // Refresh pathways after deletion
    } catch (error) {
      console.error("Failed to delete item:", error);
      toast.error("Failed to delete item. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="flex flex-col w-full min-h-screen">
      <div>
        <h1>Manage Pathways</h1>
        <p className="text-(--neutral-800)">
          Manage learning pathways, pathway enrolments, and define pathway
          types.
        </p>
      </div>
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value);
          try {
            const params = new URLSearchParams(location.search);
            params.set("tab", value);
            navigate({
              to: `${location.pathname}?${params.toString()}`,
              replace: true,
            });
          } catch (e) {
            console.warn("Failed to update tab in URL", e);
          }
        }}
        className="space-y-4 mt-3"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0">
          <TabsList className="tabs-list w-full md:w-auto">
            <TabsTrigger
              value="enrolment"
              className="w-full md:w-auto px-3 py-0.5 text-sm"
            >
              Enrolments
            </TabsTrigger>
            <TabsTrigger
              value="pathways"
              className="w-full md:w-auto px-3 py-0.5 text-sm"
            >
              Pathways
            </TabsTrigger>
            <TabsTrigger
              value="pathway-types"
              className="w-full md:w-auto px-3 py-0.5 text-sm"
            >
              Pathway Types
            </TabsTrigger>
          </TabsList>
          <div className="w-full md:w-auto mt-2 md:mt-0">
            {activeTab === "pathways" && (
              <AddPathwayDialog refetchPathways={fetchPathways} />
            )}
            {activeTab === "enrolment" && (
              <div className="flex flex-col gap-2 w-full md:flex-row md:justify-end md:w-auto">
                <div className="flex w-full md:w-auto items-stretch gap-0.5">
                  <EnrollStudentsToPathway
                    onEnrolled={fetchEnrolments}
                    triggerLabel="Enroll Students"
                    triggerVariant="default"
                    triggerClassName="flex-1 sm:flex-none w-full sm:w-auto rounded-l-md rounded-r-none bg-(--color-primary) text-white hover:bg-(--color-accent) border border-(--color-primary)"
                  />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="default"
                        className="flex-none w-12 md:w-auto rounded-l-none rounded-r-md border border-(--color-primary) bg-(--color-primary) text-white px-0 md:px-3 flex items-center justify-center"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-50">
                      <DropdownMenuItem
                        onSelect={(event) => {
                          event.preventDefault();
                          setPathwayBulkDialogOpen(true);
                        }}
                      >
                        Bulk Enroll Students
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            )}
            {activeTab === "pathway-types" && (
              <AddPathwayTypeDialog onAdded={fetchPathwayTypes} />
            )}
          </div>
        </div>
        <TabsContent value="enrolment" className="space-y-4" forceMount>
          {!shouldRenderEnrolmentTab ? null : (
            <>
              <TableToolbar
                searchTerm={enrolmentSearch}
                onSearchChange={setEnrolmentSearch}
                filters={[
                  {
                    label: "Filter by Pathway",
                    value: pendingEnrolmentCategoryFilter,
                    onChange: setPendingEnrolmentCategoryFilter,
                    onClear: () => setPendingEnrolmentCategoryFilter("all"),
                    options: enrolmentFilterOptions,
                  },
                ]}
                sorts={[
                  {
                    label: "Sort by",
                    value: pendingEnrolmentSort,
                    onChange: setPendingEnrolmentSort,
                    onClear: () => setPendingEnrolmentSort("all"),
                    options: enrolmentSortOptions,
                  },
                ]}
                onApplyFilters={() => {
                  setEnrolmentCategoryFilter(pendingEnrolmentCategoryFilter);
                  setEnrolmentPage(1);
                }}
                onApplySort={() => {
                  setEnrolmentSort(pendingEnrolmentSort);
                }}
              />

              {(!enrolmentsSearchActive && enrolments.length === 0 && (enrolmentsLoading || pathwayListLoading)) ? (
                <EnrolmentsSkeleton rows={6} />
              ) : (
                <AdminDataTable
                  columns={enrolmentColumns}
                  data={enrolmentsSearchActive ? pagedEffectiveSearchResults : filteredSortedEnrolments}
                  loading={enrolmentsLoading || pathwayListLoading}
                  headerClassName="text-sm font-heading font-semibold text-(--neutral-900)"
                  paginationMode="server"
                  currentPage={enrolmentPage}
                  rowsPerPage={enrolmentRowsPerPage}
                  totalPages={enrolmentsSearchActive ? effectiveSearchTotalPages : enrolmentsPagination.total_pages}
                  totalRows={enrolmentsSearchActive ? effectiveSearchTotalRows : enrolmentsPagination.total_count}
                  rowsPerPageOptions={[10, 25, 50]}
                  onPageChange={setEnrolmentPage}
                  onRowsPerPageChange={setEnrolmentRowsPerPage}
                  rowLabel="enrolments"
                  selectionResetKey={enrolmentsSelectionResetKey}
                  onBulkAction={(label, ctx) => {
                    const normalized = String(label || "").trim().toLowerCase();
                    const selectedRows = Array.isArray(ctx?.selectedRowsOnPage)
                      ? ctx.selectedRowsOnPage
                      : [];

                    if (selectedRows.length < 2) return;

                    if (normalized === "edit") {
                      setSelectedPathwayEnrolmentsForBatchEdit(selectedRows);
                      setBatchEditPathwayEnrolmentsOpen(true);
                      return;
                    }

                    if (normalized === "delete") {
                      setSelectedPathwayEnrolmentsForBatchDelete(selectedRows);
                      setBatchDeletePathwayEnrolmentsOpen(true);
                      return;
                    }
                  }}
                  onEdit={(row) => {
                    setSelectedPathwayEnrollmentToEdit(row);
                    setEditPathwayEnrollmentDialogOpen(true);
                  }}
                  onDelete={(row) => {
                    console.log("Row on delete:", row);
                    setSelectedPathwayEnrollment({
                      pathwayId: row.pathway_id,
                      userId: row.user_id,
                      studentName: row.name,
                    });
                    setDeletePathwayDialogOpen(true);
                  }}
                />
              )}
            </>
          )}
        </TabsContent>
        <TabsContent value="pathways" className="space-y-4" forceMount>
          {!shouldRenderPathwaysTab ? null : (
            <>
              <TableToolbar
                searchTerm={pathwaySearch}
                onSearchChange={setPathwaySearch}
                filters={[
                  {
                    label: "Filter by Type",
                    value: pendingPathwayFilter,
                    onChange: setPendingPathwayFilter,
                    onClear: () => setPendingPathwayFilter("all"),
                    options: pathwayFilterOptions,
                  },
                ]}
                sorts={[
                  {
                    label: "Sort by",
                    value: pendingPathwaySort,
                    onChange: setPendingPathwaySort,
                    onClear: () => setPendingPathwaySort("all"),
                    options: pathwaySortOptions,
                  },
                ]}
                onApplyFilters={() => {
                  setPathwaySearch(pathwaySearch);
                  setPathwayFilter(pendingPathwayFilter);
                }}
                onApplySort={() => {
                  setPathwaySort(pendingPathwaySort);
                }}
              />

              {pathways.length === 0 && loading ? (
                <EnrolmentsSkeleton rows={6} />
              ) : (
                <AdminDataTable
                  columns={pathwayColumns}
                  data={filteredSortedPathways}
                  loading={loading}
                  headerClassName="text-sm font-heading font-semibold text-(--neutral-900)"
                  selectionResetKey={pathwaysSelectionResetKey}
                  enableBulkEditAction={false}
                  onBulkAction={(label, ctx) => {
                    const normalized = String(label || "").trim().toLowerCase();
                    if (normalized !== "delete") return;

                    const selectedRows = Array.isArray(ctx?.selectedRowsOnPage)
                      ? ctx.selectedRowsOnPage
                      : [];

                    if (selectedRows.length < 2) return;

                    setSelectedPathwaysForBatchDelete(selectedRows);
                    setBatchDeletePathwaysOpen(true);
                  }}
                  onEdit={(item) => handleEdit(item, "pathway")}
                  onDelete={(item) => handleDelete(item, "pathways")}
                />
              )}
            </>
          )}
        </TabsContent>
        <TabsContent value="pathway-types" className="space-y-4" forceMount>
          {!shouldRenderPathwayTypesTab ? null : (
            <>
              <TableToolbar
                searchTerm={pathwayTypesSearch}
                onSearchChange={setPathwayTypesSearch}
                filters={[
                  {
                    label: "Filter by Type",
                    value: pendingPathwayTypesFilter,
                    onChange: setPendingPathwayTypesFilter,
                    onClear: () => setPendingPathwayTypesFilter("all"),
                    options: [
                      { value: "all", label: "All Types" },
                      ...Array.from(new Set(pathwayTypes.map((pt) => pt.type))).map(
                        (type) => ({
                          value: type,
                          label: type,
                        })
                      ),
                    ],
                  },
                ]}
                sorts={[
                  {
                    label: "Sort by",
                    value: pendingPathwayTypesSort,
                    onChange: setPendingPathwayTypesSort,
                    onClear: () => setPendingPathwayTypesSort("type"),
                    options: [
                      { value: "type", label: "Pathway Type" },
                      { value: "description", label: "Description" },
                      { value: "pathways", label: "Pathways" },
                    ],
                  },
                ]}
                onApplyFilters={() => {
                  setPathwayTypesSearch(pathwayTypesSearch);
                  setPathwayTypesFilter(pendingPathwayTypesFilter);
                }}
                onApplySort={() => {
                  setPathwayTypesSort(pendingPathwayTypesSort);
                }}
              />

              {pathwayTypes.length === 0 && pathwayTypesLoading ? (
                <EnrolmentsSkeleton rows={6} />
              ) : (
                <>
                  <DefaultTable
                    columns={pathwayTypesColumns}
                    data={paginatedPathwayTypesWithRowKeys}
                    emptyMessage="No pathway types found."
                    selectedItems={[...selectedPathwayTypeIds]}
                    onSelectItem={toggleSelectPathwayType}
                    onSelectAll={toggleSelectAllPathwayTypesOnPage}
                    bulkActions={["Delete"]}
                    onBulkAction={(label, ctx) => {
                      const normalized = String(label || "").trim().toLowerCase();
                      if (normalized !== "delete") return;

                      const selectedRows = Array.isArray(ctx?.selectedRowsOnPage)
                        ? ctx.selectedRowsOnPage
                        : [];

                      if (selectedRows.length < 2) return;

                      setSelectedPathwayTypesForBatchDelete(selectedRows);
                      setBatchDeletePathwayTypesOpen(true);
                    }}
                  />
                  <Pagination
                    currentPage={pathwayTypesPage}
                    totalPages={totalPathwayTypesPages}
                    rowsPerPage={pathwayTypesRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    onRowsPerPageChange={setPathwayTypesRowsPerPage}
                    onPageChange={setPathwayTypesPage}
                    totalRows={totalPathwayTypes}
                    rowLabel="row items"
                    selectedCount={selectedPathwayTypeIds.size}
                  />
                </>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
      <PathwayBulkEnrollDialog
        open={pathwayBulkDialogOpen}
        onOpenChange={setPathwayBulkDialogOpen}
        pathwayOptions={pathwayList.map((pathway) => ({
          id: pathway.id,
          name: pathway.title,
        }))}
        onUploaded={fetchEnrolments}
      />
      {editType === "pathway" ? (
        <EditPathwayDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          pathway={editItem}
          onEdit={() => setEditDialogOpen(false)}
          refetchPathways={fetchPathways}
        />
      ) : (
        <EditItemDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          item={editItem}
          fields={editType === "pathway" ? pathwayFields : []}
          onSave={handleSaveEdit}
          loading={loading}
        />
      )}
      <DeleteItemDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        item={deleteItem}
        itemLabel={deleteType}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
      <DeleteStudentPathwayEnrollment
        open={deletePathwayDialogOpen}
        onOpenChange={setDeletePathwayDialogOpen}
        pathwayId={selectedPathwayEnrollment?.pathwayId}
        userId={selectedPathwayEnrollment?.userId}
        studentName={selectedPathwayEnrollment?.studentName}
        onDeleted={() => {
          setDeletePathwayDialogOpen(false);
          setSelectedPathwayEnrollment(null);
          fetchEnrolments(); // Refresh enrolments after deletion
        }}
      />

      <EditPathwayEnrollmentDialog
        open={editPathwayEnrollmentDialogOpen}
        onOpenChange={(nextOpen) => {
          setEditPathwayEnrollmentDialogOpen(nextOpen);
          if (!nextOpen) setSelectedPathwayEnrollmentToEdit(null);
        }}
        enrollment={selectedPathwayEnrollmentToEdit}
        pathways={pathwayList}
        onUpdated={() => {
          setEditPathwayEnrollmentDialogOpen(false);
          setSelectedPathwayEnrollmentToEdit(null);
          fetchEnrolments();
        }}
      />

      <BatchEditPathwayEnrollmentDialog
        open={batchEditPathwayEnrolmentsOpen}
        onOpenChange={(nextOpen) => {
          setBatchEditPathwayEnrolmentsOpen(nextOpen);
          if (!nextOpen) setSelectedPathwayEnrolmentsForBatchEdit([]);
        }}
        enrolments={selectedPathwayEnrolmentsForBatchEdit}
        pathways={pathwayList}
        onUpdated={() => {
          setEnrolmentCountsByPathwayId({});
          setEnrolmentsSelectionResetKey((k) => k + 1);
          fetchEnrolments();
        }}
      />

      <BatchDeleteStudentPathwayEnrollmentDialog
        open={batchDeletePathwayEnrolmentsOpen}
        onOpenChange={(nextOpen) => {
          setBatchDeletePathwayEnrolmentsOpen(nextOpen);
          if (!nextOpen) setSelectedPathwayEnrolmentsForBatchDelete([]);
        }}
        enrolments={selectedPathwayEnrolmentsForBatchDelete}
        onDeleted={() => {
          setEnrolmentCountsByPathwayId({});
          setEnrolmentsSelectionResetKey((k) => k + 1);
          fetchEnrolments();
        }}
      />

      <BatchDeletePathwaysDialog
        open={batchDeletePathwaysOpen}
        onOpenChange={(nextOpen) => {
          setBatchDeletePathwaysOpen(nextOpen);
          if (!nextOpen) setSelectedPathwaysForBatchDelete([]);
        }}
        pathways={selectedPathwaysForBatchDelete}
        onDeleted={() => {
          setPathwaysSelectionResetKey((k) => k + 1);
          fetchPathways({ force: true });
        }}
      />

      <EditPathwayTypeDialog
        open={editPathwayTypeDialogOpen}
        onOpenChange={setEditPathwayTypeDialogOpen}
        pathwayType={selectedPathwayType}
        onUpdated={() => fetchPathwayTypes({ force: true })}
      />

      <DeletePathwayTypeDialog
        pathwayType={selectedPathwayTypeToDelete}
        open={deletePathwayTypeDialogOpen}
        onOpenChange={setDeletePathwayTypeDialogOpen}
        onDeleted={() => {
          setDeletePathwayTypeDialogOpen(false);
          setSelectedPathwayTypeToDelete(null);
          fetchPathwayTypes({ force: true });
        }}
      />

      <BatchDeletePathwayTypesDialog
        open={batchDeletePathwayTypesOpen}
        onOpenChange={(nextOpen) => {
          setBatchDeletePathwayTypesOpen(nextOpen);
          if (!nextOpen) setSelectedPathwayTypesForBatchDelete([]);
        }}
        pathwayTypes={selectedPathwayTypesForBatchDelete}
        onDeleted={() => {
          setBatchDeletePathwayTypesOpen(false);
          setSelectedPathwayTypesForBatchDelete([]);
          setSelectedPathwayTypeIds(new Set());
          fetchPathwayTypes({ force: true });
        }}
      />
    </div>
  );
}
